import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from './ToastContext';

// Create context
const UserAuthContext = createContext();

// Custom hook to use the user auth context
export const useUserAuth = () => useContext(UserAuthContext);

// API base URL
const API_URL = 'https://nmdiamond.in/api';

export const UserAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showSuccessToast, showErrorToast } = useToast();

  useEffect(() => {
    // Check if user is logged in on page load
    const token = localStorage.getItem('userToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        setCurrentUser(parsedUserData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (err) {
        console.error('Error parsing user data:', err);
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
      }
    }
    
    setLoading(false);
  }, []);

  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      
      console.log('Registering user with data:', userData);
      const response = await axios.post(`${API_URL}/auth/register.php`, userData);
      
      console.log('Registration response:', response.data);
      
      if (response.data.success) {
        const { token, user } = response.data;
        
        // Save to localStorage
        localStorage.setItem('userToken', token);
        localStorage.setItem('userData', JSON.stringify(user));
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setCurrentUser(user);
        showSuccessToast('Registration successful!');
        return user;
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (err) {
      console.error('Registration error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Registration failed';
      setError(errorMsg);
      showErrorToast(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      console.log('Logging in user:', email);
      const response = await axios.post(`${API_URL}/auth/login.php`, { email, password });
      
      console.log('Login response:', response.data);
      
      if (response.data.success) {
        const { token, user } = response.data;
        
        // Save to localStorage
        localStorage.setItem('userToken', token);
        localStorage.setItem('userData', JSON.stringify(user));
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setCurrentUser(user);
        showSuccessToast('Login successful!');
        return user;
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Login failed';
      setError(errorMsg);
      showErrorToast(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const logout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    showSuccessToast('You have been logged out');
  };
  
  const value = {
    currentUser,
    loading,
    error,
    register,
    login,
    logout,
    isAuthenticated: !!currentUser
  };
  
  return <UserAuthContext.Provider value={value}>{children}</UserAuthContext.Provider>;
};

export default UserAuthContext; 